import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, BookOpen, FlaskConical, FileText, ExternalLink, GitCommit, X as XIcon } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

const NOTIF_ICONS = {
    expertise_base_ready: BookOpen,
    scientific: FlaskConical,
    default: FileText,
};

// S50 — Suggestions Consolidation auto Niveau 1 (localStorage dismiss MVP)
const DISMISS_KEY = 'itriia_consolidation_dismissed_v1';
const DISMISS_TTL_DAYS = 14;

function loadDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return {};
        const data = JSON.parse(raw);
        const cutoff = Date.now() - DISMISS_TTL_DAYS * 86400 * 1000;
        // Purger entrées expirées
        const cleaned = {};
        for (const [k, ts] of Object.entries(data)) {
            if (typeof ts === 'number' && ts > cutoff) cleaned[k] = ts;
        }
        return cleaned;
    } catch { return {}; }
}

function dismissSession(sessionId) {
    const current = loadDismissed();
    current[String(sessionId)] = Date.now();
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(current)); } catch {}
}

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    return `il y a ${days}j`;
}

export default function NotificationBell({ onNavigate }) {
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const {
        notifications, unreadCount, loading,
        loadNotifications, handleMarkRead, handleMarkAllRead,
    } = useNotifications();

    // S50 — Suggestions Consolidation auto Niveau 1
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    const loadSuggestions = useCallback(async () => {
        setSuggestionsLoading(true);
        try {
            const token = localStorage.getItem('oracle_token');
            const res = await fetch('/api/consolidation/suggestions?limit=10', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('http_' + res.status);
            const data = await res.json();
            const dismissed = loadDismissed();
            const visible = (data.suggestions || []).filter(s => !dismissed[String(s.session_id)]);
            setSuggestions(visible);
        } catch {
            setSuggestions([]);
        }
        setSuggestionsLoading(false);
    }, []);

    const handleDismissSuggestion = (sessionId) => {
        dismissSession(sessionId);
        setSuggestions(prev => prev.filter(s => s.session_id !== sessionId));
    };

    const handleOpenSuggestion = (sug) => {
        if (onNavigate) {
            onNavigate(`/projects/${sug.project_id}#consolidate-${sug.session_id}`);
            setOpen(false);
        }
    };

    // Charger les notifs + suggestions quand on ouvre le panel
    useEffect(() => {
        if (open) {
            loadNotifications();
            loadSuggestions();
        }
    }, [open, loadNotifications, loadSuggestions]);

    // Fermer au clic extérieur
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={panelRef} style={{ position: 'relative' }}>
            {/* Bell button */}
            <button
                onClick={() => setOpen(!open)}
                title="Notifications"
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '6px', borderRadius: '6px', position: 'relative',
                    color: open ? '#3b82f6' : 'var(--text-secondary)',
                    transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={e => { if (!open) e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span style={{
                        position: 'absolute', top: '2px', right: '2px',
                        background: '#ef4444', color: 'white',
                        fontSize: '9px', fontWeight: '700',
                        minWidth: '14px', height: '14px',
                        borderRadius: '7px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px', lineHeight: 1,
                        boxShadow: '0 0 0 2px var(--header-bg)',
                    }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', right: 0,
                    width: '360px', maxHeight: 'min(560px, calc(100vh - 90px))',
                    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                    borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    zIndex: 1000, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    animation: 'fadeIn 0.15s ease',
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '14px 16px', borderBottom: '1px solid var(--card-border)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexShrink: 0,
                    }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text)' }}>
                            Notifications {unreadCount > 0 && <span style={{ color: '#3b82f6', fontWeight: '600' }}>({unreadCount})</span>}
                        </span>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: '#3b82f6', fontSize: '12px', display: 'flex',
                                    alignItems: 'center', gap: '4px', padding: '4px 8px',
                                    borderRadius: '4px',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.08)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                            >
                                <CheckCheck size={12} /> Tout lire
                            </button>
                        )}
                    </div>

                    {/* Suggestions Consolidation (S50) — au-dessus des notifs */}
                    {suggestions.length > 0 && (
                        <div style={{
                            borderBottom: '1px solid var(--card-border)',
                            background: 'rgba(59,130,246,0.03)',
                            flexShrink: 0, maxHeight: '40%', overflowY: 'auto',
                        }}>
                            <div style={{
                                padding: '8px 16px 4px',
                                fontSize: '11px', fontWeight: '600',
                                color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>
                                Suggestions de consolidation ({suggestions.length})
                            </div>
                            {suggestions.map(sug => (
                                <div
                                    key={`sug-${sug.session_id}`}
                                    style={{
                                        padding: '10px 16px 12px',
                                        display: 'flex', gap: '10px', alignItems: 'flex-start',
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    <div style={{
                                        width: '32px', height: '32px', borderRadius: '8px',
                                        background: 'rgba(59,130,246,0.12)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <GitCommit size={15} style={{ color: '#3b82f6' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: '13px', fontWeight: '600',
                                            color: 'var(--text)', lineHeight: '1.3',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            Consolider « {sug.title || `Session #${sug.session_id}`} »
                                        </div>
                                        <div style={{
                                            fontSize: '11px', color: 'var(--text-secondary)',
                                            marginTop: '2px', lineHeight: '1.4',
                                        }}>
                                            {sug.project_name} · {sug.reason}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                            <button
                                                onClick={() => handleOpenSuggestion(sug)}
                                                style={{
                                                    padding: '3px 10px', borderRadius: '6px',
                                                    background: 'rgba(59,130,246,0.12)',
                                                    border: '1px solid rgba(59,130,246,0.25)',
                                                    color: '#3b82f6', fontSize: '11px', fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                }}
                                            >
                                                <ExternalLink size={10} /> Voir
                                            </button>
                                            <button
                                                onClick={() => handleDismissSuggestion(sug.session_id)}
                                                title="Ignorer cette suggestion (14 jours)"
                                                style={{
                                                    padding: '3px 10px', borderRadius: '6px',
                                                    background: 'transparent',
                                                    border: '1px solid var(--card-border)',
                                                    color: 'var(--text-secondary)', fontSize: '11px',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                }}
                                            >
                                                <XIcon size={10} /> Ignorer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* List */}
                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        {loading && notifications.length === 0 ? (
                            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                Chargement...
                            </div>
                        ) : notifications.length === 0 ? (
                            suggestions.length === 0 ? (
                                <div style={{ padding: '32px', textAlign: 'center' }}>
                                    <Bell size={28} style={{ color: 'var(--text-secondary)', opacity: 0.4, marginBottom: '8px' }} />
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Aucune notification</div>
                                </div>
                            ) : null
                        ) : (
                            notifications.map(notif => {
                                const Icon = NOTIF_ICONS[notif.type] || NOTIF_ICONS.default;
                                const isUnread = !notif.read_at;
                                return (
                                    <div
                                        key={notif.id}
                                        onClick={() => {
                                            if (isUnread) handleMarkRead(notif.id);
                                            if (notif.link && onNavigate) {
                                                onNavigate(notif.link);
                                                setOpen(false);
                                            }
                                        }}
                                        style={{
                                            padding: '12px 16px',
                                            borderBottom: '1px solid var(--card-border)',
                                            cursor: notif.link ? 'pointer' : (isUnread ? 'pointer' : 'default'),
                                            background: isUnread ? 'rgba(59,130,246,0.04)' : 'transparent',
                                            display: 'flex', gap: '10px', alignItems: 'flex-start',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = isUnread ? 'rgba(59,130,246,0.04)' : 'transparent'}
                                    >
                                        {/* Icon */}
                                        <div style={{
                                            width: '32px', height: '32px', borderRadius: '8px',
                                            background: isUnread ? 'rgba(59,130,246,0.12)' : 'rgba(100,100,100,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <Icon size={15} style={{ color: isUnread ? '#3b82f6' : 'var(--text-secondary)' }} />
                                        </div>

                                        {/* Content */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '13px', fontWeight: isUnread ? '600' : '400',
                                                color: 'var(--text)', lineHeight: '1.3',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}>
                                                {notif.title}
                                            </div>
                                            {notif.body && (
                                                <div style={{
                                                    fontSize: '12px', color: 'var(--text-secondary)',
                                                    marginTop: '2px', lineHeight: '1.4',
                                                    display: '-webkit-box', WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                }}>
                                                    {notif.body}
                                                </div>
                                            )}
                                            <div style={{
                                                fontSize: '11px', color: 'var(--text-muted)',
                                                marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px',
                                            }}>
                                                <span>{timeAgo(notif.created_at)}</span>
                                                {notif.link && (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#3b82f6' }}>
                                                        <ExternalLink size={9} /> Voir
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Unread dot */}
                                        {isUnread && (
                                            <div style={{
                                                width: '8px', height: '8px', borderRadius: '50%',
                                                background: '#3b82f6', flexShrink: 0, marginTop: '4px',
                                            }} />
                                        )}
                                        {!isUnread && (
                                            <Check size={12} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '4px' }} />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
